/**
 * @module modules/dataframe/dataframe_context
 * @description <dataframe-repository-sk/>
 *
 * DataFrameRepository manages a local cache of the traces.
 *
 * The class loads the traces with the initial paramset and extends using the
 * same paramset. The paramset can only be reset by reloading the entire
 * traces, that is, paramset being immutable.
 *
 * @provide
 * dataframeContext: the DataFrame storing the current available data.
 *
 * @provide
 * dataframeLoadingContext: whether there is a request in flight.
 *
 * @provide
 * dataframeRepoContext: the repo object itself to manage data.
 *
 * @example
 * In the html page, you attach the <dataframe-repository-sk> as a parent node,
 * then in the child node implementation, you can request the context by:
 * @consume({context:dataframeContext})
 * dataframe: DataFrame
 *
 * this.dataframe will be assigned whenever there is a change.
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: all[name]
    });
}
_export(exports, {
    sliceRange: function() {
        return sliceRange;
    },
    dataframeContext: function() {
        return dataframeContext;
    },
    dataTableContext: function() {
        return dataTableContext;
    },
    traceColorMapContext: function() {
        return traceColorMapContext;
    },
    dataframeAnomalyContext: function() {
        return dataframeAnomalyContext;
    },
    dataframeUserIssueContext: function() {
        return dataframeUserIssueContext;
    },
    dataframeLoadingContext: function() {
        return dataframeLoadingContext;
    },
    dataframeRepoContext: function() {
        return dataframeRepoContext;
    },
    DataFrameRepository: function() {
        return DataFrameRepository;
    }
});
const _loader = require("@google-web-components/google-chart/loader");
const _context = require("@lit/context");
const _lit = require("lit");
const _decorators = require("lit/decorators.js");
const _index = require("./index");
const _query = require("../../../infra-sk/modules/query");
const _json = require("../json");
const _plotbuilder = require("../common/plot-builder");
const _paramtools = require("../paramtools");
const _const = require("../const/const");
const _dataservice = require("../data-service/data-service");
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var _DataFrameRepository;
// Shift the range by offset.
// Note, shitf [0, 10] by 10 will give [1, 11].
const deltaRange = (range, offset)=>{
    const { begin, end } = range;
    if (offset > 0) {
        return {
            begin: end + 1,
            end: end + offset + 1
        };
    } else {
        return {
            begin: begin + offset - 1,
            end: begin - 1
        };
    }
};
const sliceRange = ({ begin, end }, chunkSize)=>{
    if (chunkSize <= 0) {
        return [
            {
                begin,
                end
            }
        ];
    }
    // The range is inclusive on both ends
    const span = end - begin + 1;
    const slices = Math.ceil(span / chunkSize);
    // Shortcut as this will be most of cases.
    if (slices === 1) {
        return [
            {
                begin,
                end
            }
        ];
    }
    // The first (n - 1) slices and then concat the last slice as the last one
    // usually doesn't have the same length.
    return Array.from({
        length: slices - 1
    }, (_, k)=>{
        return {
            begin: begin + k * chunkSize,
            end: begin + (k + 1) * chunkSize - 1
        };
    }).concat({
        begin: begin + (slices - 1) * chunkSize,
        end: end
    });
};
const dataframeContext = (0, _context.createContext)(Symbol('dataframe-context'));
const dataTableContext = (0, _context.createContext)(Symbol('datatable-context'));
const traceColorMapContext = (0, _context.createContext)(Symbol('trace-color-map-context'));
const dataframeAnomalyContext = (0, _context.createContext)(Symbol('dataframe-anomaly-context'));
const dataframeUserIssueContext = (0, _context.createContext)(Symbol('dataframe-user-issue-context'));
const dataframeLoadingContext = (0, _context.createContext)(Symbol('dataframe-loading-context'));
const dataframeRepoContext = (0, _context.createContext)(Symbol('dataframe-repo-context'));
const emptyResolver = (_1)=>{};
const MAX_DATAPOINTS = 20000;
let DataFrameRepository = (_DataFrameRepository = class DataFrameRepository extends _lit.LitElement {
    // This element doesn't render anything and all the children should be
    // attached to itself directly.
    // Note, <slot></slot> is not needed if there is no shadow root.
    createRenderRoot() {
        return this;
    }
    get paramset() {
        return this._paramset;
    }
    get traces() {
        return this._traceset;
    }
    get header() {
        return this._header;
    }
    get queryString() {
        return (0, _query.fromParamSet)(this.paramset);
    }
    get isEmpty() {
        const header = this.header;
        return !header || header.length === 0;
    }
    getRange(key) {
        if (this.isEmpty) {
            return {
                begin: 0,
                end: 0
            };
        }
        const header = this.header;
        const len = header.length;
        if (len > 1) {
            return {
                begin: header[0][key],
                end: header[len - 1][key]
            };
        } else {
            return {
                begin: header[0][key],
                end: header[0][key]
            };
        }
    }
    get commitRange() {
        return this.getRange('offset');
    }
    get timeRange() {
        return this.getRange('timestamp');
    }
    addTraceInfo(header, traceset, traceMetadata, truncate = true) {
        if (header.length <= 0) {
            return;
        }
        // TODO(haowoo): this now is simply to prepend or append the given data,
        //  however, we should also try to replace duplicate if there is time
        //  overlap, or insert into middle of the dataset. This is not needed at
        //  the moment.
        const isAfter = header[0].timestamp >= this.timeRange.end;
        if (isAfter) {
            this._header = (this.header || []).concat(header);
        } else {
            this._header = header.concat(this.header);
        }
        let traceHead = (0, _json.TraceSet)({}), traceTail = this.traces;
        if (isAfter) {
            [traceHead, traceTail] = [
                traceTail,
                traceHead
            ];
        }
        Object.keys(this.traces).forEach((key)=>{
            this._traceset[key] = (traceHead[key] || []).concat(...traceset[key]).concat(traceTail[key] || []);
        });
        if (truncate && this._header.length > MAX_DATAPOINTS) {
            const excess = this._header.length - MAX_DATAPOINTS;
            // We always discard the oldest data (from the beginning of the array)
            // to prioritize preserving the most recent data and the current view.
            this._header.splice(0, excess);
            Object.keys(this._traceset).forEach((key)=>{
                this._traceset[key].splice(0, excess);
            });
        }
        if (traceMetadata !== null) {
            if (this._traceMetadata === null) {
                this._traceMetadata = [];
            }
            traceMetadata.forEach((traceData)=>{
                let exists = false;
                for(let i = 0; i < this._traceMetadata.length; i++){
                    if (this._traceMetadata[i].traceid === traceData.traceid) {
                        exists = true;
                        Object.keys(traceData.commitLinks).forEach((commitIdKey)=>{
                            const commitId = parseInt(commitIdKey);
                            if (!(commitId in this._traceMetadata[i].commitLinks)) {
                                // This commit is not in the existing metadata, so let's add it.
                                this._traceMetadata[i].commitLinks[commitId] = traceData.commitLinks[commitId];
                            }
                        });
                    }
                    if (!exists) {
                        // This means the trace is not present in existing metadata,
                        // so let's add the entire thing.
                        this._traceMetadata.push(traceData);
                    }
                }
            });
        }
    }
    /**
   * Returns a Promise that resolves when the ongoing request completes.
   */ get requestComplete() {
        return this._requestComplete;
    }
    generateFrameRequest({ begin, end }) {
        return {
            begin: begin,
            end: end,
            request_type: 0,
            queries: [
                this.queryString
            ],
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
    }
    async requestNewRange(range) {
        if (!this._baseRequest) {
            this._baseRequest = this.generateFrameRequest(range);
        }
        const req = structuredClone(this._baseRequest);
        const now = Math.floor(Date.now() / 1000);
        if (range.begin > now) {
            return {};
        }
        if (range.end > now) {
            range.end = now;
        }
        req.begin = range.begin;
        req.end = range.end;
        if (req.begin === req.end) {
            return {};
        }
        try {
            const resp = await _dataservice.DataService.getInstance().sendFrameRequest(req, {
                onMessage: (msg)=>{
                    throw new Error(msg);
                },
                pollingIntervalMs: 1000
            });
            return resp;
        } catch (e) {
            if (e instanceof _dataservice.DataServiceError) {
                console.log('request range (', new Date(range.begin * 1000), new Date(range.end * 1000), ') failed with msg:', e.message);
                return {};
            }
            if (e.message) {
                return Promise.reject(e.message);
            }
            console.log('fetch frame response failed.', e);
            return {};
        }
    }
    async setDataFrame(df) {
        this.dataframe = df;
        await DataFrameRepository.loadPromise;
        // We could possibly merge with new data w/o recreating the entire table.
        const data = (0, _plotbuilder.convertFromDataframe)(df, 'both');
        if (data) {
            this.data = google.visualization.arrayToDataTable(data);
        } else {
            this.data = null;
        }
    }
    /**
   * Get the Anomaly.
   *
   * @param trace The trace name, typically this is the test name.
   * @param commit The commit position
   * @returns The Anomaly if it exits at given position, null otherwise.
   */ getAnomaly(trace, commit) {
        if (!this.anomaly) {
            return null;
        }
        const traceAnomalies = this.anomaly[trace];
        var _ref;
        return (_ref = traceAnomalies && traceAnomalies[commit]) !== null && _ref !== void 0 ? _ref : null;
    }
    getAllAnomalies() {
        return this.anomaly;
    }
    /**
   * Update anomaly map.
   *
   * @param anomalies The list of anomaly to be merged.
   * @param id List of original revisions to be compared against.
   */ updateAnomalies(anomalies, id) {
        this.anomaly = (0, _index.removeAnomaly)(this.anomaly, id);
        this.anomaly = (0, _index.mergeAnomaly)(this.anomaly, anomalies);
    }
    /**
   * Reset the dataframe and its corresponding FrameRequest.
   *
   * This can be used to refill the data if it is from a different source, and this
   * can take over and extend the time frame.
   *
   * @param dataframe The full dataframe from the request.
   * @param request The completed FrameRequet that was sent for the dataframe.
   */ async resetWithDataframeAndRequest(dataframe, anomalies, request, replaceAnomalies = false) {
        this._baseRequest = request;
        this._baseRequest.request_type = 0; // change to timestamp-based query.
        let newAnomalyMap;
        if (replaceAnomalies) {
            newAnomalyMap = (0, _index.mergeAnomaly)(null, anomalies);
        } else {
            newAnomalyMap = (0, _index.mergeAnomaly)(this.anomaly, anomalies);
        }
        this._header = dataframe.header || [];
        this._traceset = dataframe.traceset;
        this._traceMetadata = dataframe.traceMetadata;
        await this.setDataFrame(dataframe);
        this.anomaly = newAnomalyMap;
    }
    /**
   * Reset and load the dataframe with the ParamSet.
   *
   * @param range The requested timestamp range in seconds.
   * @param param The paramset to fetch the dataframe.
   * @returns The promise that resolves to the length of traces when the
   *  request completes.
   */ async resetTraces(range, param) {
        var _resp_dataframe, _resp_dataframe1, _resp_dataframe2, _resp_dataframe_header, _resp_dataframe3;
        let resolver = emptyResolver;
        const curRequest = this._requestComplete;
        this._requestComplete = new Promise((resolve)=>{
            resolver = resolve;
        });
        await curRequest;
        this.loading = true;
        this._paramset = param;
        const resp = await this.requestNewRange(range);
        this._traceset = ((_resp_dataframe = resp.dataframe) === null || _resp_dataframe === void 0 ? void 0 : _resp_dataframe.traceset) || (0, _json.TraceSet)({});
        this._header = ((_resp_dataframe1 = resp.dataframe) === null || _resp_dataframe1 === void 0 ? void 0 : _resp_dataframe1.header) || [];
        this._traceMetadata = ((_resp_dataframe2 = resp.dataframe) === null || _resp_dataframe2 === void 0 ? void 0 : _resp_dataframe2.traceMetadata) || [];
        const totalTraces = ((_resp_dataframe3 = resp.dataframe) === null || _resp_dataframe3 === void 0 ? void 0 : (_resp_dataframe_header = _resp_dataframe3.header) === null || _resp_dataframe_header === void 0 ? void 0 : _resp_dataframe_header.length) || 0;
        await this.setDataFrame({
            traceset: this._traceset,
            header: this._header,
            paramset: this._paramset,
            skip: 0,
            traceMetadata: this._traceMetadata
        });
        this.anomaly = (0, _index.mergeAnomaly)(this.anomaly, resp.anomalymap);
        this.loading = false;
        resolver(totalTraces);
        return totalTraces;
    }
    /**
   * Prepend or append additional traces to the dataframe using the same
   * ParamSet.
   *
   * @param offsetInSeconds The offset extending, positive means forward,
   *  negative means backward.
   * @returns The promise that resolves to the length of additional traces when
   *  the request completes.
   */ async extendRange(offsetInSeconds, truncate = true) {
        let totalTraces = 0;
        let resolver = emptyResolver;
        const curRequest = this._requestComplete;
        this._requestComplete = new Promise((resolve)=>{
            resolver = resolve;
        });
        try {
            await curRequest;
            this.loading = true;
            const range = deltaRange(this.timeRange, offsetInSeconds);
            const ranges = sliceRange(range, this.chunkSize);
            const allResponses = [];
            for(let slice = 0; slice < ranges.length; slice++){
                allResponses.push(this.requestNewRange(ranges[slice]));
            }
            // Fetch and sort the frame responses so they can appended consecutively.
            const sortedResponses = (await Promise.allSettled(allResponses)).filter((result)=>result.status === 'fulfilled').map((result)=>result.value).filter(// Filter responses with valid dataframes with actual traces.
            (fr)=>{
                var _fr_dataframe_header;
                return fr.dataframe && (((_fr_dataframe_header = fr.dataframe.header) === null || _fr_dataframe_header === void 0 ? void 0 : _fr_dataframe_header.length) || 0) > 0 && Object.keys(fr.dataframe.traceset).length > 0;
            }).sort((a, b)=>a.dataframe.header[0].offset - b.dataframe.header[0].offset);
            totalTraces = sortedResponses.map((resp)=>{
                var _resp_dataframe_header;
                return ((_resp_dataframe_header = resp.dataframe.header) === null || _resp_dataframe_header === void 0 ? void 0 : _resp_dataframe_header.length) || 0;
            }).reduce((count, cur)=>count + cur, 0);
            const header = [].concat(...sortedResponses.map((resp)=>resp.dataframe.header));
            const traceset = (0, _json.TraceSet)({});
            Object.keys(this.traces).forEach((key)=>{
                traceset[key] = (traceset[key] || []).concat(...sortedResponses.map((resp)=>{
                    if (resp.dataframe && resp.dataframe.traceset[key]) {
                        return resp.dataframe.traceset[key];
                    } else {
                        var _resp_dataframe_header, _resp_dataframe;
                        var _resp_dataframe_header_length;
                        // If one of the traces we're trying to expand is not in the response,
                        // this will cause the traceset to not have the same length as the
                        // header, shifting all datapoints. Instead, we need to pad the trace
                        // with MISSING values so that they're in sync with the header.
                        const numNulls = (_resp_dataframe_header_length = (_resp_dataframe = resp.dataframe) === null || _resp_dataframe === void 0 ? void 0 : (_resp_dataframe_header = _resp_dataframe.header) === null || _resp_dataframe_header === void 0 ? void 0 : _resp_dataframe_header.length) !== null && _resp_dataframe_header_length !== void 0 ? _resp_dataframe_header_length : 0;
                        return Array(numNulls).fill(_const.MISSING_DATA_SENTINEL);
                    }
                }));
            });
            const traceMetadata = [];
            sortedResponses.map((resp)=>{
                if (resp.dataframe && resp.dataframe.traceMetadata) {
                    traceMetadata.push(...resp.dataframe.traceMetadata);
                }
            });
            this.addTraceInfo(header, traceset, traceMetadata, truncate);
            const anomaly = sortedResponses.reduce((pre, cur)=>(0, _index.mergeAnomaly)(pre, cur.anomalymap), {});
            await this.setDataFrame({
                traceset: this._traceset,
                header: this._header,
                paramset: this._paramset,
                skip: 0,
                traceMetadata: this._traceMetadata
            });
            this.anomaly = (0, _index.mergeAnomaly)(this.anomaly, anomaly);
        } finally{
            this.loading = false;
            // This is needed to ensure that the resolver is called even if the
            // request fails, otherwise the caller can get stuck.
            resolver(totalTraces);
            return totalTraces;
        }
    }
    async shift(commitRange, offset = -200) {
        const req = deltaRange(commitRange, offset);
        return await _dataservice.DataService.getInstance().shift(req);
    }
    // Makes an API call to fetch the comments in the given commit position range.
    // Modifies the response to match with the UserIssueMap interface.
    async getUserIssues(traceKeys, begin, end) {
        // If trace key has special functions like norm(,a=A,b=B,), remove
        // those functions from the traceKey. This is done to make sure
        // the user issue is reflected for the normalized, etc transformations of
        // the graph.
        const modifiedTraceKeys = traceKeys.map((k)=>(0, _paramtools.formatSpecialFunctions)(k)).map((trace)=>{
            return ',' + trace + ',';
        });
        const req = {
            trace_keys: modifiedTraceKeys,
            begin_commit_position: begin,
            end_commit_position: end
        };
        const resp = await _dataservice.DataService.getInstance().getUserIssues(req);
        const output = {};
        if (resp.UserIssues) {
            resp.UserIssues.forEach((issue)=>{
                const traceKey = issue.TraceKey;
                const commitPos = issue.CommitPosition;
                const issueId = issue.IssueId;
                if (!(traceKey in output)) {
                    output[traceKey] = {};
                }
                output[traceKey][commitPos] = {
                    bugId: issueId,
                    x: -1,
                    y: -1
                };
            });
        }
        this.userIssues = output;
        return output;
    }
    // Updates the userIssues property if a new buganizer issue is created
    // or an existing issue is removed.
    updateUserIssue(traceKey, commitPosition, bugId) {
        let issues = this.userIssues;
        this.userIssues = null;
        // If trace key has special functions like norm(,a=A,b=B,), remove
        // those functions from the traceKey. This is done to make sure
        // the user issue is reflected for the normalized, etc transformations of
        // the graph.
        const modifiedTraceKey_preTempFix = (0, _paramtools.formatSpecialFunctions)(traceKey);
        // TODO(b/469649488) remove leading / trailing commas from the DB
        const modifiedTraceKey = ',' + modifiedTraceKey_preTempFix + ',';
        const updatedIssue = {
            bugId: bugId,
            x: -1,
            y: -1
        };
        if (issues === null) {
            issues = {};
            issues[modifiedTraceKey] = {};
            issues[modifiedTraceKey][commitPosition] = updatedIssue;
            this.userIssues = issues;
            return;
        }
        if (Object.keys(issues).includes(modifiedTraceKey)) {
            issues[modifiedTraceKey][commitPosition] = updatedIssue;
            this.userIssues = issues;
        } else {
            issues[modifiedTraceKey] = {};
            issues[modifiedTraceKey][commitPosition] = updatedIssue;
            this.userIssues = issues;
        }
    }
    /**
   * Clears all the data in the repository.
   */ clear() {
        this._traceset = (0, _json.TraceSet)({});
        this._header = [];
        this._traceMetadata = [];
        this.dataframe = {
            traceset: (0, _json.TraceSet)({}),
            header: [],
            paramset: (0, _json.ReadOnlyParamSet)({}),
            skip: 0,
            traceMetadata: []
        };
        this.data = null;
        this.anomaly = null;
        this.userIssues = null;
    }
    /**
   * Clears just the anomaly map.
   */ clearAnomalyMap() {
        this.anomaly = null;
    }
    constructor(...args){
        super(...args);
        this._paramset = (0, _json.ReadOnlyParamSet)({});
        this._traceset = (0, _json.TraceSet)({});
        this._traceMetadata = [];
        this._header = [];
        // When the requests are more than this size, we slice into separate requests
        // so they run concurrently to improve data fetching performance.
        this.chunkSize = 30 * 24 * 60 * 60 // 1 month in seconds.
        ;
        this.dataframe = {
            traceset: (0, _json.TraceSet)({}),
            header: [],
            paramset: (0, _json.ReadOnlyParamSet)({}),
            skip: 0,
            traceMetadata: []
        };
        this.data = null;
        this.loading = false;
        this.dfRepo = this;
        this.anomaly = null;
        this.userIssues = null;
        // Track the current ongoing request.
        // Initiazlied as resolved because there are no requests.
        this._requestComplete = Promise.resolve(0);
    }
}, (()=>{
    // The promise that resolves when the Google Chart API is loaded.
    _DataFrameRepository.loadPromise = (0, _loader.load)();
})(), _DataFrameRepository);
_ts_decorate([
    (0, _context.provide)({
        context: dataframeContext
    }),
    _ts_metadata("design:type", typeof _json.DataFrame === "undefined" ? Object : _json.DataFrame)
], DataFrameRepository.prototype, "dataframe", void 0);
_ts_decorate([
    (0, _context.provide)({
        context: dataTableContext
    }),
    _ts_metadata("design:type", typeof DataTable === "undefined" ? Object : DataTable)
], DataFrameRepository.prototype, "data", void 0);
_ts_decorate([
    (0, _context.provide)({
        context: dataframeLoadingContext
    })
], DataFrameRepository.prototype, "loading", void 0);
_ts_decorate([
    (0, _context.provide)({
        context: dataframeRepoContext
    })
], DataFrameRepository.prototype, "dfRepo", void 0);
_ts_decorate([
    (0, _context.provide)({
        context: dataframeAnomalyContext
    }),
    _ts_metadata("design:type", typeof _json.AnomalyMap === "undefined" ? Object : _json.AnomalyMap)
], DataFrameRepository.prototype, "anomaly", void 0);
_ts_decorate([
    (0, _context.provide)({
        context: dataframeUserIssueContext
    }),
    _ts_metadata("design:type", typeof UserIssueMap === "undefined" ? Object : UserIssueMap)
], DataFrameRepository.prototype, "userIssues", void 0);
DataFrameRepository = _ts_decorate([
    (0, _decorators.customElement)('dataframe-repository-sk')
], DataFrameRepository);
